import React, {useContext, useEffect, useState} from 'react';
import {CButton, CCard, CCardBody, CCol, CForm, CFormInput, CSpinner} from "@coreui/react";
import ListProducts from "../components/lists/ListProducts";
import ProductService from "../services/product.service";
import ProductFactory from "../models/ProductFactory";
import {StateContext} from "../components/context/StateProvider";
import ModalIncidencia from "../features/incidencias/components/modals/ModalIncidencia";
import NotesService from "../services/notes.service";
import Toasts from "../components/notificacion/Toasts";
import {INCIDENCIA_ESTADO, INCIDENCIA_ESTADO_VALOR, INCIDENCIA_ORIGEN, INCIDENCIA_TIPO} from "../config/constants";


/**
 * Construye los datos del modal de incidencia a partir de un producto.
 *
 * DECISION: `producto.Note` solo existe si el artículo YA tiene incidencia
 * abierta (ver handleSubmit). Si no la tiene se parte de un objeto vacío:
 * handleConfirmNote ya distingue por `id_note` si toca crear o actualizar, y
 * el empleado lo elige el usuario en el propio modal.
 */
const construirNotaModal = (producto, referencia) => {
    const nota = producto.Note ?? {};

    return {
        codigo_articulo: referencia,
        id_empleado: nota.id_empleado ?? producto.employee,
        nombre_empleado: nota.nombre_empleado ?? producto.nameEmployee,
        estado: nota.estado ?? INCIDENCIA_ESTADO_VALOR.ABIERTA,
        origen: INCIDENCIA_ORIGEN.IVENTARIO,
        note: '',
        modificado: Date.now(),
        creado: Date.now(),
        comentarios: nota.comentarios ?? [],
        id_note: nota.id_note,
        tipo_note: INCIDENCIA_TIPO.ART
    };
};

const Inventory = () => {

    const {updateState, state} = useContext(StateContext);
    const [loading, setLoading] = useState(false);
    const [referencia, setReferencia] = useState('');
    const [showNote, setShowNote] = useState(false);
    const [title, setTitle] = useState('');
    const [text, setText] = useState('');
    const [currentNote, setCurrentNote] = useState(undefined);
    const [modalNoteMode, setModalNoteMode] = useState(0);
    const [modalNoteAdmin, setModalNoteAdmin] = useState(0);
    const [product, setProduct] = useState(0);

    useEffect(() => {
        return () => {
            updateState({ toasts: [] });
            updateState({ products: [] });
        };
    }, []);

    const handleChange = (event) => {
        setReferencia(event.target.value);
    }

    const handleSubmit = async (event) => {

        if (event) {
            event.preventDefault();
        }


        if (referencia !== "") {

            setLoading(true);

            await ProductService.getProductInventory(referencia).then(
                async (data) => {
                    const productMap = data.map(async (product) => {

                        let productInventory = ProductFactory.product(product.CODIGO,
                            product.BarrasModels,
                            product.NOMBRE,
                            product.UBICACION,
                            product.VENSERIE,
                            product.Art_FotoModels,
                            product.MulticamModels,
                            product.Stocks2Models,
                            null
                        );
                        productInventory.inventory = product.INVENTARIO;

                        const currentNote = await NotesService.getNoteByReferencia(product.CODIGO, INCIDENCIA_ORIGEN.IVENTARIO, INCIDENCIA_ESTADO.ABIERTAS);
                        productInventory.currentNote = null;
                        if (currentNote) {
                            productInventory.Note = currentNote;
                        }

                        return productInventory;
                    });

                    const productsMapFull = await Promise.all(productMap);

                    updateState({products: productsMapFull});
                    setLoading(false);
                }
            );
        }
    }

    const handleClose = async () => {
        setShowNote(false);
    }

    const handleNote = (productRef) => {
        setShowNote(true);
        let currentProduct = state.products.find((row) => row.ref === productRef);

        let modalNote = construirNotaModal(currentProduct, productRef);

        setCurrentNote(modalNote);
        setText('Datos de la incidencia <strong>'+ productRef +'</strong>');
        setTitle('Atención');
        setProduct(currentProduct);
    }

    const handleConfirmNote = async (modalNote) => {

        //Si Note existe, actualizamos y creamos como comentario añadido
        //Si no existe creamos nueva note + comentario
        if(product.Note && product.Note.id_note){
            NotesService.registerNotesComentarios(modalNote).then(
                (resp) => {

                    product.Note.comentarios.unshift(resp.data)

                    NotesService.updateNote(modalNote).then(
                        (resp) => {
                            let toasts = Toasts.generateToast(resp.message, 'success');
                            updateState({toasts: [...state.toasts, toasts]});
                            updateState({products: state.products.map((row) => (row.ref === product.ref ? product : row))})
                            setShowNote(false);
                        }
                    ).catch((error) => {
                        const toasts = Toasts.generateToast(error.message, 'error');
                        updateState({toasts: [...state.toasts, toasts]})
                    });
                }
            ).catch((error) => {
                const toasts = Toasts.generateToast(error.message, 'error');
                updateState({toasts: [...state.toasts, toasts]})
            });
        }else{

            NotesService.registerNotes(modalNote).then(
                (resp) => {
                    modalNote.id_note = resp.data.id_note
                    product.Note = resp.data
                    product.Note.comentarios = []
                    NotesService.registerNotesComentarios(modalNote).then(
                        (resp) => {
                            product.Note.comentarios.unshift(resp.data)
                            let toasts = Toasts.generateToast(resp.message, 'success');
                            updateState({toasts: [...state.toasts, toasts]});
                            updateState({products: state.products.map((row) => (row.ref === product.ref ? product : row))})
                            setShowNote(false);
                        }
                    ).catch((error) => {
                        const toasts = Toasts.generateToast(error.message, 'error');
                        updateState({toasts: [...state.toasts, toasts]})
                    });
                }
            ).catch((error) => {
                const toasts = Toasts.generateToast(error.message, 'error');
                updateState({toasts: [...state.toasts, toasts]})
            });
        }
    }

    return (
        <CCol id="inventory" md={12}>

            <ModalIncidencia show={showNote}
                       text={text}
                       noteModal={currentNote}
                       modalNoteMode={modalNoteMode}
                       modalNoteAdmin={modalNoteAdmin}
                       handleClose={() => handleClose()}
                       handleConfirm={(product) => handleConfirmNote(product)}></ModalIncidencia>

            <CCard className="p-3">
                <CCardBody>
                    <CForm onSubmit={(e) => handleSubmit(e)} className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="referencia" className="form-label">Referencia</label>
                            <CFormInput className="form-control" type="text" id="referencia" name="referencia"
                                        onChange={(e) => handleChange(e)}/>
                        </div>
                        <CButton type="submit" color="primary" className="px-4 col-12">
                            {loading ?
                                <CSpinner component="span" size="sm"
                                          aria-hidden="true"/> : 'Buscar'}
                        </CButton>
                    </CForm>
                    <ListProducts updateProducts="0" selectProducts="0" print={false} inventory={true} handleNote={handleNote}/>
                </CCardBody>
            </CCard>
        </CCol>
    );
}

export default Inventory;